import { Paperclip, XIcon } from "lucide-react";
import { useFileUpload } from "ui/hooks/use-file-upload";
import { Button } from "ui/components/button";
import { assetUrl } from "utils/index";
import { useEffect, useRef } from "react";

export default function ChatAttachmentUpload({ value, onChange, disabled, shouldReset, preview = false, accept = "image/*,video/*" }) {
    const [{ files }, { removeFile, openFileDialog, getInputProps }] =
        useFileUpload({
            accept,
            onFilesChange: (selectedFiles) => {
                if (onChange) onChange(selectedFiles[0] || null);
            },
        });

    const prevShouldReset = useRef(shouldReset);

    useEffect(() => {
        if (shouldReset && !prevShouldReset.current && files.length > 0) {
            files.forEach(file => {
                if (file?.id) {
                    removeFile(file.id);
                }
            });
        }
        prevShouldReset.current = shouldReset;
    }, [shouldReset, files, removeFile]);

    const previewUrl =
        files[0]?.preview || (typeof value === "string" && value ? assetUrl(value) : null);
    const fileName = files[0]?.file?.name || null;
    const fileType = files[0]?.file?.type || (typeof value === "object" && value?.type) || "";

    const isVideo = fileType.startsWith("video/");
    const isImage = fileType.startsWith("image/");

    return (
        <div className="flex flex-col gap-2">
            {/* Attachment Button */}
            <div className="flex items-center gap-2">
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={openFileDialog}
                    disabled={disabled || !!previewUrl}
                    aria-label="Attach file"
                >
                    <Paperclip className="h-4 w-4" />
                </Button>

                {fileName && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="truncate max-w-[200px]">{fileName}</span>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => {
                                removeFile(files[0]?.id);
                                if (onChange) onChange(null);
                            }}
                            aria-label="Remove attachment"
                        >
                            <XIcon className="h-3 w-3" />
                        </Button>
                    </div>
                )}
            </div>

            {/* Preview */}
            {preview && previewUrl && (
                <div className="relative inline-block max-w-md">
                    {isVideo ? (
                        <video
                            src={previewUrl}
                            className="max-h-48 rounded-lg border"
                            controls
                        >
                            Your browser does not support the video tag.
                        </video>
                    ) : isImage ? (
                        <img
                            src={previewUrl}
                            alt="Attachment preview"
                            className="max-h-48 rounded-lg border object-contain"
                        />
                    ) : (
                        <div className="p-4 border rounded-lg bg-gray-50 flex items-center gap-2">
                            <Paperclip className="h-4 w-4 text-gray-400" />
                            <a
                                href={previewUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline font-medium"
                            >
                                View Document
                            </a>
                        </div>
                    )}

                    <Button
                        type="button"
                        onClick={() => {
                            removeFile(files[0]?.id);
                            if (onChange) onChange(null);
                        }}
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white hover:bg-gray-100 shadow border"
                        aria-label="Remove attachment"
                    >
                        <XIcon className="h-3 w-3 text-gray-700" />
                    </Button>
                </div>
            )}

            <input
                {...getInputProps()}
                className="sr-only"
                aria-label="Upload file"
                tabIndex={-1}
            />
        </div>
    );
}